import React, { memo, useMemo } from 'react'
import { Box } from '@material-ui/core'
import { LAYER_ASSET_HEIGHT } from 'constant'
import { DRAGNDROP_TYPE } from '~/enums'
import { getItemStyles } from './getItemStyles'
import styles from './draggingAsset.module.scss'

import { useTimelineSnapping } from './lib/useTimelineSnapping'
import { getExternalSourceElement } from './lib/getExternalSourceElement'
import { getSourceItemSourceOffset } from './lib/getSourceItemSourceOffset'
import { useDraggingAsset } from './lib/useDraggingAsset'
import { useMultipleDragging } from './lib/useMultipleDragging'
import { useInitialDragData } from './lib/useInitialDragData'
import { TimelineScrollPositionContext } from '~/components/Timeline/ScrollPositionContext'
import { SnappedPreview } from './SnappedPreview'
import { CustomDragLayerContext } from '~/components/CustomDragLayerContext'
import { getDragElementsContainerWidth } from './lib/getDragElementsContainerWidth'
import { getDragElementsContainerHeight } from './lib/getDragElementsContainerHeight'
import { getTransformTranslateXY } from './lib/getTransformTranslateXY'
import { useSliderTime } from '~/providers/SliderTimeContextProvider'


// Dragged item should change it's look when it's over layer.
export const DraggingAsset = memo(({
  overNewLayerIndex,
}) => {
  const {
    item: initialDragData,
    clientOffset,
    sourceOffset,
    initialClientOffset,
  } = React.useContext(CustomDragLayerContext)
  const {
    setSliderSticky,
    timelineMouseDown,
  } = React.useContext(TimelineScrollPositionContext)

  const localTime = useSliderTime()

  const {
    isMultipleDrag,
    isDraggingItemOverLayer,
    isOverItem,
    detachedMode,
    scale,
    timelineTopOffset,
    scrollTop,
    timelineLeftOffset,
    scrollLeft,
    targetLayerIndex,
    restElementsByLayers,
    dragItem,
    dragAssetsWidth,
    sliderTimeX,
    isExternalSource,
    multipleDragAssets,
    draggableBlockDuration,
    isMultipleSourceMediaFiles,
  } = useInitialDragData({ clientOffset, localTime, initialDragData })

  const {
    draggingAsset,
    offset,
    draggingAssetWidth,
  } = useDraggingAsset({ dragItem, scale, isExternalSource })

  const currentSourceOffset = isExternalSource
    ? getSourceItemSourceOffset(
      clientOffset, dragItem, detachedMode, isMultipleSourceMediaFiles
    )
    : sourceOffset

  const {
    draggableBlockLeftX,
    draggableBlockY,
    transitions,
    multipleSnappedPreview,
  } = useMultipleDragging({
    dragItem,
    clientOffset,
    sourceOffset: currentSourceOffset,
    initialClientOffset,
    scale,
    overNewLayerIndex,
    timelineTopOffset,
    scrollTop,
    targetLayerIndex,
    timelineLeftOffset,
    scrollLeft,
    isMultipleDrag,
    sliderTimeX,
    dragAssetsWidth,
    setSliderSticky,
    timelineMouseDown,
    isExternalSource,
    multipleDragAssets,
    draggableBlockDuration,
  })


  const { x, y, intersection, snappedPreview } = useTimelineSnapping({
    item: dragItem,
    clientOffset,
    sourceOffset: currentSourceOffset,
    width: draggingAssetWidth,
    ignoredTypes: [ DRAGNDROP_TYPE.TRANSITION_ITEM, DRAGNDROP_TYPE.LAYER_TRANSITION_ASSET ],
    offset,
    isDraggingItemOverLayer,
    overNewLayerIndex,
    targetLayerIndex,
    restElementsByLayers,
    timelineTopOffset,
    scrollTop,
    timelineLeftOffset,
    scrollLeft,
    isMultipleDrag,
    sliderTimeX,
    dragAssetsWidth,
    setSliderSticky,
    timelineMouseDown,
    isExternalSource,
  })

  const showSnappedPreview = !overNewLayerIndex
    && ((intersection
    && !isOverItem.size
    && snappedPreview?.width < draggingAssetWidth)
   || (multipleSnappedPreview?.width && isMultipleDrag))

  const currentX = isMultipleDrag ? draggableBlockLeftX : x
  const currentY = isMultipleDrag ? draggableBlockY : y
  const currentDragAssets = isMultipleDrag ? multipleDragAssets : draggingAsset

  const currentSnappedPreviewX = isMultipleDrag ? multipleSnappedPreview.x : snappedPreview?.x
  const currentSnappedPreviewY = isMultipleDrag ? multipleSnappedPreview.y : snappedPreview?.y
  const currentSnappedPreviewWidth = isMultipleDrag
    ? multipleSnappedPreview.width
    : snappedPreview?.width


  const { x: transformX, y: transformY } = useMemo(() => getTransformTranslateXY(
    snappedPreview, draggingAssetWidth,
    currentX, currentY, isMultipleDrag
  ), [
    snappedPreview,
    draggingAssetWidth,
    currentX,
    currentY,
    isMultipleDrag,
  ])

  const previewWidth = useMemo(() => getDragElementsContainerWidth({
    scale, draggingAsset: currentDragAssets,
  }), [ scale, currentDragAssets ])

  const previewHeight = useMemo(() => getDragElementsContainerHeight({
    draggingAsset: currentDragAssets,
  }), [ currentDragAssets ])

  return (
    <div style={{ position: 'relative' }}>
      {
      (dragItem.type !== DRAGNDROP_TYPE.TRANSITION_ITEM
        && (!overNewLayerIndex
          || !detachedMode
          || (isMultipleDrag && !isExternalSource))
      )
        ? (
          <SnappedPreview
            scale={scale}
            currentDragAssets={currentDragAssets}
            visible={!!clientOffset}
            isMultipleDrag={isMultipleDrag}
            offset={offset}
            dragItem={dragItem}
            transitions={transitions}
            previewWidth={previewWidth}
            previewHeight={previewHeight}
            transformX={transformX}
            transformY={transformY}
            isExternalSource={isExternalSource}
          />
        )
        : (
          <div style={{ ...getItemStyles({
            clientOffset,
            height: LAYER_ASSET_HEIGHT,
            x,
            y,
          }),
          position: 'relative' }}
          >
            <Box display="flex">
              {[].concat(currentDragAssets).map(draggingAsset => (
                getExternalSourceElement(dragItem, draggingAsset, styles)
              ))}
            </Box>
          </div>
        )
      }
      {showSnappedPreview ? (
        <div
          className={styles.previewItem}
          style={{ position: 'absolute',
            top: '2px',
            ...getItemStyles({ x: currentSnappedPreviewX,
              clientOffset,
              width: currentSnappedPreviewWidth,
              y: currentSnappedPreviewY,
              height: LAYER_ASSET_HEIGHT }) }}
        />
      ) : null}
    </div>
  )
})
